import { Injectable } from '@angular/core';
import { TelegramUser } from '@ibc-shared/models/telegram/telegram-user.model';
import { TelegramWebApp } from '@ibc-shared/models/telegram/telegram-web-app.model';
import { BehaviorSubject, Observable } from 'rxjs';

declare global {
  interface Window {
    Telegram?: {
      WebApp: TelegramWebApp;
    };
  }
}

@Injectable({
  providedIn: 'root',
})
export class TelegramService {
  private telegramWebApp: TelegramWebApp | null = null;
  private isInitialized$ = new BehaviorSubject<boolean>(false);
  private telegramUser$ = new BehaviorSubject<TelegramUser | null>(null);
  private initData$ = new BehaviorSubject<string | null>(null);

  constructor() {
    this.initialize();
  }

  private initialize(): void {
    const win =
      typeof window !== 'undefined' ? window : typeof globalThis !== 'undefined' ? globalThis : {};
    const global = win as Window & { Telegram?: { WebApp?: TelegramWebApp } };
    const hasTelegram = !!global.Telegram;
    const hasWebApp = !!global.Telegram?.WebApp;
    const webApp = global.Telegram?.WebApp;

    console.log('[ACFE] TelegramService.initialize:', {
      hasGlobalThis: typeof globalThis !== 'undefined',
      hasWindow: typeof window !== 'undefined',
      isIframe: typeof window !== 'undefined' && window !== window.top,
      hasTelegram,
      hasWebApp,
      telegramKeys: hasTelegram ? Object.keys(global.Telegram || {}) : [],
      conditionPassed: !!webApp,
    });

    if (webApp) {
      this.telegramWebApp = webApp;
      this.telegramWebApp.ready();
      this.isInitialized$.next(true);

      // Extract and store initData
      const initData = this.telegramWebApp.initData;
      console.log(
        '[ACFE] TelegramService.initialize: initData present?',
        !!initData,
        'length:',
        initData?.length ?? 0,
      );
      if (initData) {
        this.initData$.next(initData);
      } else {
        console.warn(
          '[ACFE] TelegramService.initialize: initData is empty - app may hang waiting for it',
        );
      }

      // Extract user data
      const user = this.telegramWebApp.initDataUnsafe?.user;
      if (user) {
        this.telegramUser$.next(user);
      }
    } else {
      console.warn('[ACFE] TelegramService.initialize: Telegram WebApp not available', {
        hasGlobalThis: typeof globalThis !== 'undefined',
        hasWindow: typeof window !== 'undefined',
        isIframe: typeof window !== 'undefined' && window !== window.top,
        hasTelegram,
        hasWebApp,
        telegramKeys: hasTelegram ? Object.keys(global.Telegram || {}) : [],
      });
    }
  }

  isAvailable(): boolean {
    return this.telegramWebApp !== null;
  }

  /**
   * Get initialization status
   */
  getInitializationStatus(): Observable<boolean> {
    return this.isInitialized$.asObservable();
  }

  /**
   * Get Telegram user data
   */
  getTelegramUser(): TelegramUser | null {
    return this.telegramUser$.value;
  }

  /**
   * Get Telegram user data as Observable
   */
  getTelegramUser$(): Observable<TelegramUser | null> {
    return this.telegramUser$.asObservable();
  }

  /**
   * Get initData string (for backend validation)
   * Returns stored initData from BehaviorSubject
   */
  getInitData(): string | null {
    return this.initData$.value;
  }

  /**
   * Get initData as Observable
   */
  getInitData$(): Observable<string | null> {
    return this.initData$.asObservable();
  }

  /**
   * Check if initData is ready
   */
  isInitDataReady(): boolean {
    return this.initData$.value !== null;
  }

  /**
   * Get unsafe initData object
   */
  getInitDataUnsafe(): TelegramWebApp['initDataUnsafe'] | null {
    return this.telegramWebApp?.initDataUnsafe || null;
  }

  /**
   * Expand the Mini App to full height
   */
  expand(): void {
    this.telegramWebApp?.expand();
  }

  /**
   * Close the Mini App
   */
  close(): void {
    this.telegramWebApp?.close();
  }

  /**
   * Set header color
   */
  setHeaderColor(color: string): void {
    this.telegramWebApp?.setHeaderColor(color);
  }

  /**
   * Set background color
   */
  setBackgroundColor(color: string): void {
    this.telegramWebApp?.setBackgroundColor(color);
  }

  /**
   * Get current theme (light/dark)
   */
  getColorScheme(): 'light' | 'dark' | null {
    return this.telegramWebApp?.colorScheme || null;
  }

  /**
   * Get theme parameters
   */
  getThemeParams(): TelegramWebApp['themeParams'] | null {
    return this.telegramWebApp?.themeParams || null;
  }

  /**
   * Get viewport height
   */
  getViewportHeight(): number {
    return this.telegramWebApp?.viewportHeight || 0;
  }

  /**
   * Get stable viewport height
   */
  getViewportStableHeight(): number {
    return this.telegramWebApp?.viewportStableHeight || 0;
  }

  /**
   * Show alert
   */
  showAlert(message: string, callback?: () => void): void {
    this.telegramWebApp?.showAlert(message, callback);
  }

  /**
   * Show confirmation dialog
   */
  showConfirm(message: string, callback?: (confirmed: boolean) => void): void {
    this.telegramWebApp?.showConfirm(message, callback);
  }

  /**
   * Show popup with custom buttons
   */
  showPopup(
    params: {
      title?: string;
      message: string;
      buttons?: {
        id?: string;
        type?: 'default' | 'ok' | 'close' | 'cancel' | 'destructive';
        text: string;
      }[];
    },
    callback?: (id: string) => void,
  ): void {
    this.telegramWebApp?.showPopup(params, callback);
  }

  /**
   * Open external link
   */
  openLink(url: string, options?: { try_instant_view?: boolean }): void {
    this.telegramWebApp?.openLink(url, options);
  }

  /**
   * Open Telegram link
   */
  openTelegramLink(url: string): void {
    this.telegramWebApp?.openTelegramLink(url);
  }

  /**
   * Send data to bot
   */
  sendData(data: string): void {
    this.telegramWebApp?.sendData(data);
  }

  /**
   * Enable closing confirmation
   */
  enableClosingConfirmation(): void {
    this.telegramWebApp?.enableClosingConfirmation();
  }

  /**
   * Disable closing confirmation
   */
  disableClosingConfirmation(): void {
    this.telegramWebApp?.disableClosingConfirmation();
  }

  /**
   * Haptic feedback - impact
   */
  hapticImpact(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' = 'medium'): void {
    this.telegramWebApp?.HapticFeedback.impactOccurred(style);
  }

  /**
   * Haptic feedback - notification
   */
  hapticNotification(type: 'error' | 'success' | 'warning'): void {
    this.telegramWebApp?.HapticFeedback.notificationOccurred(type);
  }

  /**
   * Haptic feedback - selection changed
   */
  hapticSelectionChanged(): void {
    this.telegramWebApp?.HapticFeedback.selectionChanged();
  }

  /**
   * Main Button - Set text
   */
  setMainButtonText(text: string): void {
    this.telegramWebApp?.MainButton.setText(text);
  }

  /**
   * Main Button - Show
   */
  showMainButton(): void {
    this.telegramWebApp?.MainButton.show();
  }

  /**
   * Main Button - Hide
   */
  hideMainButton(): void {
    this.telegramWebApp?.MainButton.hide();
  }

  /**
   * Main Button - Enable
   */
  enableMainButton(): void {
    this.telegramWebApp?.MainButton.enable();
  }

  /**
   * Main Button - Disable
   */
  disableMainButton(): void {
    this.telegramWebApp?.MainButton.disable();
  }

  /**
   * Main Button - Set onClick handler
   */
  onMainButtonClick(callback: () => void): void {
    this.telegramWebApp?.MainButton.onClick(callback);
  }

  /**
   * Main Button - Remove onClick handler
   */
  offMainButtonClick(callback: () => void): void {
    this.telegramWebApp?.MainButton.offClick(callback);
  }

  /**
   * Main Button - Set parameters
   */
  setMainButtonParams(params: {
    text?: string;
    color?: string;
    text_color?: string;
    is_active?: boolean;
    is_visible?: boolean;
  }): void {
    this.telegramWebApp?.MainButton.setParams(params);
  }

  /**
   * Main Button - Show progress
   */
  showMainButtonProgress(leaveActive?: boolean): void {
    this.telegramWebApp?.MainButton.showProgress(leaveActive);
  }

  /**
   * Main Button - Hide progress
   */
  hideMainButtonProgress(): void {
    this.telegramWebApp?.MainButton.hideProgress();
  }

  /**
   * Back Button - Show
   */
  showBackButton(): void {
    this.telegramWebApp?.BackButton.show();
  }

  /**
   * Back Button - Hide
   */
  hideBackButton(): void {
    this.telegramWebApp?.BackButton.hide();
  }

  /**
   * Back Button - Set onClick handler
   */
  onBackButtonClick(callback: () => void): void {
    this.telegramWebApp?.BackButton.onClick(callback);
  }

  /**
   * Back Button - Remove onClick handler
   */
  offBackButtonClick(callback: () => void): void {
    this.telegramWebApp?.BackButton.offClick(callback);
  }

  /**
   * Check if version is at least specified version
   */
  isVersionAtLeast(version: string): boolean {
    return this.telegramWebApp?.isVersionAtLeast(version) || false;
  }

  /**
   * Get platform
   */
  getPlatform(): string | null {
    return this.telegramWebApp?.platform || null;
  }

  /**
   * Get version
   */
  getVersion(): string | null {
    return this.telegramWebApp?.version || null;
  }

  /**
   * Read text from clipboard
   */
  readTextFromClipboard(callback?: (text: string) => void): void {
    this.telegramWebApp?.readTextFromClipboard(callback);
  }

  /**
   * Request write access
   */
  requestWriteAccess(callback?: (granted: boolean) => void): void {
    this.telegramWebApp?.requestWriteAccess(callback);
  }

  /**
   * Request contact access
   */
  requestContact(callback?: (granted: boolean) => void): void {
    this.telegramWebApp?.requestContact(callback);
  }

  /**
   * Get raw Telegram WebApp instance (for advanced usage)
   */
  getWebApp(): TelegramWebApp | null {
    return this.telegramWebApp;
  }
}
